import React, { useState } from "react";
import { Sparkles, Loader2, Search, Trophy } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { StoryRow } from "@/components/newsroom/StoryRow";

const LOCATIONS = ["Bermuda", "New York", "Miami", "Toronto", "London", "Los Angeles", "Chicago", "Atlanta", "Kingston", "Barbados"];

export const SectionTab = ({ section, title, subtitle, chips, chipMode, categories, fetchLabel, hasLocation, stories, reload, accent = "from-blue-600 to-cyan-500" }) => {
  const [chip, setChip] = useState("");
  const [location, setLocation] = useState("");
  const [customLoc, setCustomLoc] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [castBusy, setCastBusy] = useState(false);
  const [cast, setCast] = useState(null);

  const makeSportscast = async () => {
    setCastBusy(true);
    try {
      const r = await api.post("/newsroom/sportscast", { topic: chip });
      setCast(r.data);
      toast.success("Sportscast ready — script written and voiced");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sportscast failed");
    } finally { setCastBusy(false); }
  };

  const doFetch = async (topicOverride) => {
    setBusy(true);
    try {
      let r;
      if (section === "music") {
        r = await api.post("/news/ingest");
      } else {
        const loc = customLoc.trim() || location;
        r = await api.post("/newsroom/fetch", { section, topic: topicOverride ?? chip, location: loc });
      }
      toast.success(`Fetched ${r.data.inserted} new stories (${r.data.skipped} already in)`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fetch failed");
    } finally { setBusy(false); }
  };

  const pickChip = (id) => {
    const next = chip === id ? "" : id;
    setChip(next);
    if (chipMode === "topic" && next) {
      const hasAny = (stories || []).some((s) => categories.includes(s.category) && (s.genres || []).includes(next));
      const canFetch = section !== "local" || customLoc.trim() || location;
      if (!hasAny && canFetch && !busy) doFetch(next);
    }
  };

  let list = (stories || []).filter((s) => categories.includes(s.category));
  if (chip) list = list.filter((s) => (s.genres || []).includes(chip));
  if (query.trim()) {
    const q = query.toLowerCase();
    list = list.filter((s) => (s.headline || "").toLowerCase().includes(q) || (s.summary || "").toLowerCase().includes(q));
  }

  return (
    <div data-testid={`newsroom-${section}-tab`}>
      <div className="flex flex-wrap items-start gap-4 mb-5">
        <div className="flex-1 min-w-[240px]">
          <h2 className="font-display text-xl font-bold text-ink-primary">{title}</h2>
          <p className="text-xs text-ink-muted mt-0.5">{subtitle}</p>
        </div>
        {section === "sports" && (
          <button onClick={makeSportscast} disabled={castBusy} data-testid="newsroom-sportscast-btn"
            className="inline-flex items-center gap-2 rounded-xl border border-brand-amber/50 bg-brand-amber/10 px-4 py-2.5 text-sm font-bold text-brand-amber hover:bg-brand-amber/20 transition disabled:opacity-60">
            {castBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4" />}
            {castBusy ? "Writing & voicing…" : `Make ${chip ? chip.toUpperCase() : "Sports"} Sportscast`}
          </button>
        )}
        <button onClick={() => doFetch()} disabled={busy} data-testid={`newsroom-${section}-fetch-btn`}
          className={`inline-flex items-center gap-2 rounded-xl bg-gradient-to-r ${accent} px-4 py-2.5 text-sm font-bold text-white hover:brightness-110 transition disabled:opacity-60`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {fetchLabel}
        </button>
      </div>

      {hasLocation && (
        <div className="rounded-xl border border-line bg-bg-surface p-4 mb-5" data-testid={`newsroom-${section}-location`}>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">Select Location</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {LOCATIONS.map((l) => (
              <button key={l} onClick={() => { setLocation(l); setCustomLoc(""); }} data-testid={`newsroom-loc-${l.replace(/\s/g, "-").toLowerCase()}`}
                className={`rounded-full px-3 py-1.5 text-xs transition ${location === l && !customLoc ? "bg-brand-amber/15 border border-brand-amber/50 text-ink-primary font-semibold" : "border border-line text-ink-secondary hover:text-ink-primary"}`}>
                {l}
              </button>
            ))}
          </div>
          <input value={customLoc} onChange={(e) => setCustomLoc(e.target.value)} data-testid={`newsroom-${section}-custom-loc`}
            placeholder="Or type a custom location…"
            className="w-full rounded-lg border border-line bg-bg-elevated px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/50" />
        </div>
      )}

      {cast && (
        <div className="rounded-xl border border-brand-amber/40 bg-bg-surface p-4 mb-5" data-testid="newsroom-sportscast-result">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber">Voiced Sportscast Ready</div>
            <button onClick={() => setCast(null)} className="text-xs text-ink-muted hover:text-ink-primary transition" data-testid="newsroom-sportscast-close">Dismiss</button>
          </div>
          <div className="text-sm font-semibold mb-2">{cast.label}</div>
          <audio controls className="w-full h-9 mb-3" src={`${API_BASE}/imaging/assets/${cast.asset_id}/audio`} data-testid="newsroom-sportscast-audio" />
          <details>
            <summary className="text-xs text-ink-secondary cursor-pointer hover:text-ink-primary">View script · built from {(cast.used_stories || []).length} real stories</summary>
            <pre className="mt-2 whitespace-pre-wrap text-xs text-ink-secondary font-sans max-h-48 overflow-y-auto">{cast.script}</pre>
          </details>
          <div className="text-[11px] text-ink-muted mt-2">Saved to AI Anchor bulletins — also ready to queue in SAM Broadcaster → Send Content.</div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setChip("")} data-testid={`newsroom-${section}-chip-all`}
          className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${!chip ? "bg-brand-amber text-black" : "border border-line text-ink-secondary hover:text-ink-primary"}`}>
          All
        </button>
        {chips.map((c) => (
          <button key={c.id} onClick={() => pickChip(c.id)} data-testid={`newsroom-${section}-chip-${c.id}`}
            className={`rounded-full px-3.5 py-1.5 text-xs transition ${chip === c.id ? "bg-brand-amber text-black font-semibold" : "border border-line text-ink-secondary hover:text-ink-primary"}`}>
            {c.label}
          </button>
        ))}
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-muted" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search stories…" data-testid={`newsroom-${section}-search`}
          className="w-full rounded-lg border border-line bg-bg-surface pl-9 pr-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/50" />
      </div>

      <div className="space-y-3">
        {list.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line p-12 text-center text-sm text-ink-muted">
            {busy ? "Fetching the latest stories…"
              : `No ${chip ? chip.toUpperCase() + " " : ""}${section} stories yet. ${hasLocation ? 'Select a location and click "' + fetchLabel + '".' : `Fetch the latest above.`}`}
          </div>
        ) : list.slice(0, 30).map((s) => <StoryRow key={s.story_id} s={s} />)}
      </div>
    </div>
  );
};
